import { Json } from "@redotech/json/json";
import { useHandler } from "@redotech/react-util/hook";
import {
  AssistantMessageContent,
  createToolResultOutput,
  Message,
  UserMessageContent,
} from "@redotech/redo-agents-common/llm-messages";
import { AiFileAnalysis } from "@redotech/redo-common-model/file/uploaded-file";
import { ObjectId } from "bson";
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { extractUploadErrorMessage } from "../../../image-upload-utils";
import { ToolComponentProps } from "../types/tool-component-props";

export interface AssetFile {
  id: string;
  file: File;
  previewUrl?: string;
  fileType?: "image" | "video" | "html" | "eml";
  analysis?: AiFileAnalysis;
}

export interface ToolResponseArgs {
  toolCallId: string;
  toolName: string;
  result: Record<string, any>;
}

export interface ToolResponseOptions {
  /**
   * Sometimes we want to interact with a tool without triggering the llm call, but just appending the response to the conversation.
   */
  skipLlmCall?: boolean;

  /**
   * Sometimes we want to skip the tool result callback, for example when we are programatically creating a tool call and result.
   */
  skipHandleToolResultsCallback?: boolean;

  /**
   * Optional tool call message to prepend before the response.
   * This is used when the tool call and result are being programatically created.
   */
  prependToolCall?: Message;
}

/**
 * Streaming event types for AI chat streaming support.
 * These events are yielded by the streamMessageToAI function.
 */
export type AiChatStreamEvent =
  | { type: "text-delta"; textDelta: string }
  | {
      type: "tool-call";
      toolCall: { toolCallId: string; toolName: string; input: unknown };
    }
  | { type: "reasoning"; text: string }
  | { type: "status"; status: string }
  | { type: "complete"; messages: Message[] }
  | { type: "error"; error: string };

/**
 * `placeholder` - Will show a preview in the preview area.
 * `preview` - Will show a preview in the preview area.
 */
export type PreviewStatus = "placeholder" | "preview";

/**
 * `idle` - No ongoing process.
 * `loading` - Waiting for LLM response.
 * `uploading` - Uploading files.
 * `streaming` - Streaming response from LLM.
 * `generating` - Used for long running tool calls to render a better loading state.
 * `error` - An error occurred during the process.
 */
export type ResponseStatus =
  | "idle"
  | "loading"
  | "uploading"
  | "streaming"
  | "generating"
  | "error";

interface AiChatContextType {
  // Human support mode - when true, loading indicators are suppressed
  isHumanSupportMode: boolean;

  // Assets state
  assets: AssetFile[];
  addAssets: (newAssets: AssetFile[]) => void;
  removeAsset: (id: string) => void;
  clearAssets: () => void;

  // File upload function
  uploadFiles: (files: File[]) => Promise<void>;

  // File upload options
  fileUploadOptions: FileUploadOptions;

  // Uploader property - just to check if it exists for showing/hiding UI elements
  uploader?: (params: {
    file: File;
    signal: AbortSignal;
  }) => Promise<{ url: URL; analysis?: AiFileAnalysis }>;

  // Input state
  inputValue: string;
  setInputValue: (value: string) => void;

  // Send message function
  sendMessage: (message?: string) => void;
  stopStream: () => void;

  // Voice recording state and functions
  isRecording: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => void;

  // Speech-to-text handler
  onSpeechToText?: (params: {
    audioBlob: Blob;
    signal: AbortSignal;
  }) => Promise<{ text: string }>;

  // Tool response function
  sendToolResponse: (
    response: ToolResponseArgs,
    options?: ToolResponseOptions,
  ) => Promise<void>;

  // Tool callbacks
  onToolResult?: (args: {
    messages: Message[];
    toolName: string;
    result: any;
    setPreviewStatus: (status: PreviewStatus) => void;
    setResponseStatus: (status: ResponseStatus) => void;
  }) => Promise<void>;

  // Messages state
  messages: Message[];
  addMessage: (message: Message) => void;
  updateMessage: (id: string, message: Message) => void;
  clearMessages: () => void;

  // Response status
  responseStatus: ResponseStatus;

  // Loading status message for dynamic status updates during tool execution
  loadingStatusMessage?: string;

  // Preview status
  previewStatus: PreviewStatus;
  setPreviewStatus: (status: PreviewStatus) => void;

  // Additional tool components
  additionalToolComponents?: {
    [key: string]:
      | ((args: ToolComponentProps) => JSX.Element | null)
      | undefined;
  };
  markdownLinkComponent?: (props: {
    href: string;
    children?: ReactNode;
  }) => JSX.Element | null;
  markdownTextTransform?: (text: string) => string;

  // Message feedback handler
  onMessageFeedback?: (feedback: {
    messageId: string;
    conversationId?: string;
    feedback: "positive" | "negative" | undefined;
    comment?: string;
  }) => Promise<void>;

  // Conversation ID for feedback
  conversationId?: string;
}

// Create the context with default values
export const AiChatContext = createContext<AiChatContextType>({
  isHumanSupportMode: false,

  assets: [],
  addAssets: () => {},
  removeAsset: () => {},
  clearAssets: () => {},
  uploadFiles: async () => {},

  fileUploadOptions: {},
  uploader: undefined,

  inputValue: "",
  setInputValue: () => {},
  sendMessage: () => {},
  stopStream: () => {},

  isRecording: false,
  startRecording: async () => {},
  stopRecording: () => {},
  onSpeechToText: undefined,

  sendToolResponse: async () => {},

  onToolResult: async () => {},

  messages: [],
  addMessage: () => {},
  updateMessage: () => {},
  clearMessages: () => {},

  responseStatus: "idle",

  loadingStatusMessage: undefined,

  previewStatus: "placeholder",
  setPreviewStatus: () => {},

  additionalToolComponents: {},

  onMessageFeedback: undefined,
  conversationId: undefined,
});

/**
 * Configuration options for file upload functionality
 */
export interface FileUploadOptions {
  /**
   * Accept attribute for the file input element (e.g. "image/*,.html,.eml")
   */
  inputAccept?: string;

  /**
   * Whether to allow multiple file selection
   */
  inputMultiple?: boolean;

  /**
   * Custom label for the upload button
   */
  inputLabel?: string;

  /**
   * Additional menu items to display in the media menu dropdown
   * Each item should have a label, icon component, and onClick handler
   */
  additionalMenuItems?: {
    label: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    onClick: () => void;
  }[];
}

interface AiChatProviderProps {
  children: ReactNode;
  /**
   * When true, suppresses loading indicators for messages.
   * Use this when connected to human support instead of AI.
   */
  isHumanSupportMode?: boolean;
  onToolResult?: (args: {
    messages: Message[];
    toolName: string;
    result: any;
    setPreviewStatus: (status: PreviewStatus) => void;
    setResponseStatus: (status: ResponseStatus) => void;
  }) => Promise<void>;
  /**
   * Whether to automatically start the chat if there is an initial message available.
   * Will only auto start if there is exactly one user default message in the messages state.
   *
   * Defaults to `true`.
   */
  autoStart?: boolean;
  /**
   * A unique conversation ID used for saving/loading conversation state
   */
  conversationId?: string;
  /**
   * Default messages to initialize the conversation
   */
  defaultMessages?: Message[];
  defaultPreviewStatus?: PreviewStatus;
  /**
   * Additional tool components map to be merged with the default tool components
   * Useful for product-specific tools that shouldn't be in the core component map
   */
  additionalToolComponents?: {
    [key: string]:
      | ((args: ToolComponentProps) => JSX.Element | null)
      | undefined;
  };
  markdownLinkComponent?: (props: {
    href: string;
    children?: ReactNode;
  }) => JSX.Element | null;
  markdownTextTransform?: (text: string) => string;
  /**
   * Function to send messages to the AI service (non-streaming).
   * Either sendMessageToAI or streamMessageToAI must be provided.
   */
  sendMessageToAI?: (params: {
    messages: Message[];
    newMessages: Message[];
  }) => Promise<{ messages: Message[]; skipLoadingIndicator?: boolean }>;
  /**
   * Streaming function to send messages to the AI service.
   * Returns an async iterator that yields streaming events.
   * Either sendMessageToAI or streamMessageToAI must be provided.
   */
  streamMessageToAI?: (params: {
    messages: Message[];
    newMessages: Message[];
    signal: AbortSignal;
  }) => AsyncIterableIterator<AiChatStreamEvent>;
  /**
   * Optional file uploader function. If not provided, file upload functionality
   * (including chat media menu and image pasting) will be disabled.
   */
  uploader?: (params: {
    file: File;
    signal: AbortSignal;
  }) => Promise<{ url: URL; analysis?: AiFileAnalysis }>;
  /**
   * Configuration options for file upload functionality
   */
  fileUploadOptions?: FileUploadOptions;
  /**
   * Optional speech-to-text function. If not provided, voice recording functionality will be disabled.
   */
  onSpeechToText?: (params: {
    audioBlob: Blob;
    signal: AbortSignal;
  }) => Promise<{ text: string }>;
  /**
   * Optional callback for when users provide feedback on AI messages
   */
  onMessageFeedback?: (feedback: {
    messageId: string;
    conversationId?: string;
    feedback: "positive" | "negative" | undefined;
    comment?: string;
  }) => Promise<void>;
}

export const AiChatProvider: React.FC<AiChatProviderProps> = ({
  children,
  autoStart = true,
  isHumanSupportMode = false,
  onToolResult,
  conversationId,
  defaultPreviewStatus = "placeholder",
  defaultMessages = [],
  additionalToolComponents = {},
  markdownLinkComponent,
  markdownTextTransform,
  sendMessageToAI,
  streamMessageToAI,
  uploader,
  onSpeechToText,
  onMessageFeedback,
  fileUploadOptions = {
    inputAccept: "image/*,.html,.eml",
    inputMultiple: true,
    inputLabel: "Upload from computer",
  },
}) => {
  // If uploader is not provided, we'll use a no-op version that won't do anything

  // Assets state
  const [assets, setAssets] = useState<AssetFile[]>([]);

  // Input state
  const [inputValue, setInputValue] = useState("");

  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null,
  );

  // Response status state
  const [responseStatus, setResponseStatus] = useState<ResponseStatus>("idle");

  // Loading status message for dynamic status updates during tool execution
  const [loadingStatusMessage, setLoadingStatusMessage] = useState<
    string | undefined
  >(undefined);

  // Preview status state
  const [previewStatus, setPreviewStatus] =
    useState<PreviewStatus>(defaultPreviewStatus);

  // Messages state - initialize from default messages if provided
  const [messages, setMessages] = useState<Message[]>(defaultMessages);

  // AbortController for stopping the stream
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);

  // Generic error message function
  const addErrorMessage = useCallback((errorMessage: string) => {
    // Add the error message as an assistant message
    setMessages((prevMessages) => [
      ...prevMessages,
      {
        id: new ObjectId().toString(),
        role: "assistant",
        content: `⚠️ **Error**: ${errorMessage}`,
      },
    ]);
  }, []);

  // Voice recording functions
  const startRecording = useCallback(async () => {
    if (!onSpeechToText) {
      console.warn(
        "No speech-to-text handler provided, voice recording skipped",
      );
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const audioChunks: Blob[] = [];

      recorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunks, { type: "audio/wav" });

        // Set loading state while transcribing
        setResponseStatus("uploading");

        try {
          const controller = new AbortController();
          const result = await onSpeechToText({
            audioBlob,
            signal: controller.signal,
          });

          // Append the transcribed text to the current input
          setInputValue((prev) => prev + (prev ? " " : "") + result.text);
        } catch (error) {
          console.error("Speech-to-text error:", error);
          addErrorMessage(
            "Failed to convert speech to text. Please try again.",
          );
        } finally {
          // Reset loading state after transcription is complete
          setResponseStatus("idle");
        }

        // Clean up the stream
        for (const track of stream.getTracks()) {
          track.stop();
        }
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      console.error("Error starting recording:", error);
      addErrorMessage(
        "Failed to access microphone. Please check your permissions.",
      );
    }
  }, [onSpeechToText, addErrorMessage]);

  const stopRecording = useCallback(() => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
      setMediaRecorder(null);
      setIsRecording(false);
    }
  }, [mediaRecorder]);

  // File upload function
  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!files.length) {
        return;
      }

      // If no uploader is provided, we can't upload files
      if (!uploader) {
        console.warn(
          "No uploader provided to AiChatProvider, file upload skipped",
        );
        return;
      }

      // Set status to uploading when uploads start
      setResponseStatus("uploading");

      const controller = new AbortController();

      // Create temporary asset placeholders immediately for display
      const tempAssets: AssetFile[] = files.map((file) => ({
        file,
        id: new ObjectId().toString(),
        // No previewUrl initially - this will trigger the loading state
      }));

      // Add the temporary assets to state immediately
      setAssets((prevAssets) => [...prevAssets, ...tempAssets]);

      // Create upload promises for all files to process them concurrently
      const uploadPromises = files.map(async (file) => {
        try {
          const fileId = tempAssets.find((asset) => asset.file === file)?.id;
          if (!fileId) {
            return null;
          }

          // Determine file type
          const isDocument =
            file.type === "text/html" ||
            file.name.endsWith(".html") ||
            file.name.endsWith(".eml") ||
            file.type === "message/rfc822";
          const isVideo = file.type.startsWith("video/");

          const uploadResult = await uploader({
            file,
            signal: controller.signal,
          });

          if (uploadResult) {
            let fileType: "image" | "video" | "html" | "eml" = "image";
            if (isDocument) {
              fileType = file.name.endsWith(".eml") ? "eml" : "html";
            } else if (isVideo) {
              fileType = "video";
            }

            return {
              fileId,
              previewUrl: uploadResult.url.toString(),
              fileType,
              analysis: uploadResult.analysis,
            };
          }
          return null;
        } catch (error) {
          console.error("Upload error:", error);
          addErrorMessage(extractUploadErrorMessage(error));
          return null;
        }
      });

      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises);

      // Update assets with all successful upload results
      setAssets((prevAssets) => {
        return prevAssets.map((asset) => {
          const result = results.find((r) => r?.fileId === asset.id);
          if (result) {
            return {
              ...asset,
              previewUrl: result.previewUrl,
              fileType: result.fileType as "image" | "video" | "html" | "eml",
              analysis: result.analysis,
            };
          }
          return asset;
        });
      });

      // Set status back to idle when all uploads are complete
      setResponseStatus("idle");
    },
    [uploader, addErrorMessage, setResponseStatus],
  );

  // Asset management functions
  const addAssets = useCallback((newAssets: AssetFile[]) => {
    setAssets((prevAssets) => [...prevAssets, ...newAssets]);
  }, []);

  const removeAsset = useCallback((id: string) => {
    setAssets((prevAssets) => {
      const updatedAssets = prevAssets.filter((asset) => asset.id !== id);

      // Cleanup preview URLs when removing assets
      const assetToRemove = prevAssets.find((asset) => asset.id === id);
      if (assetToRemove?.previewUrl) {
        URL.revokeObjectURL(assetToRemove.previewUrl);
      }

      return updatedAssets;
    });
  }, []);

  const clearAssets = useCallback(() => {
    // Cleanup all preview URLs
    for (const asset of assets) {
      if (asset.previewUrl) {
        URL.revokeObjectURL(asset.previewUrl);
      }
    }

    setAssets([]);
  }, [assets, setAssets]);

  // Temporary save conversation to localStorage
  const saveConversationToLocalStorage = useCallback(
    (updatedMessages: Message[]) => {
      if (!conversationId) {
        return;
      }

      try {
        localStorage.setItem(conversationId, JSON.stringify(updatedMessages));
      } catch (error) {
        console.error("Error saving conversation to localStorage:", error);
      }
    },
    [conversationId],
  );

  // Message management functions
  const addMessage = useCallback(
    (message: Message) => {
      setMessages((prev) => {
        const updatedMessages = [...prev, message];
        if (conversationId) {
          saveConversationToLocalStorage(updatedMessages);
        }
        return updatedMessages;
      });
    },
    [conversationId, saveConversationToLocalStorage],
  );

  // Update an existing message
  const updateMessage = useCallback(
    (id: string, updatedMessage: Message) => {
      setMessages((prev) => {
        const updatedMessages = prev.map((msg) =>
          msg.id === id ? updatedMessage : msg,
        );
        if (conversationId) {
          saveConversationToLocalStorage(updatedMessages);
        }
        return updatedMessages;
      });
    },
    [conversationId, saveConversationToLocalStorage],
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    if (conversationId) {
      try {
        localStorage.removeItem(conversationId);
      } catch (error) {
        console.error("Error removing conversation from localStorage:", error);
      }
    }
  }, [conversationId]);

  // Function to stop the current stream
  const stopStream = useCallback(() => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setResponseStatus("idle");
    }
  }, [abortController, setResponseStatus]);

  // Append multiple messages to existing messages
  const appendMessages = useCallback(
    (messagesToAppend: Message[]) => {
      setMessages((prev) => {
        const updatedMessages = [...prev, ...messagesToAppend];
        if (conversationId) {
          saveConversationToLocalStorage(updatedMessages);
        }
        return updatedMessages;
      });
    },
    [conversationId, saveConversationToLocalStorage],
  );

  // Process tool results from messages
  const handleToolResults = useCallback(
    async (_messages: Message[]) => {
      if (!onToolResult) {
        return;
      }

      // Process each message
      await Promise.all(
        _messages.map(async (message) => {
          if (message.role === "tool") {
            // Process each content item in parallel
            await Promise.all(
              message.content.map(async (content) => {
                if (content.type === "tool-result") {
                  await onToolResult({
                    messages,
                    toolName: content.toolName,
                    result: content.output,
                    setPreviewStatus,
                    setResponseStatus,
                  });
                }
              }),
            );
          }
        }),
      );
    },
    [messages, onToolResult, setPreviewStatus, setResponseStatus],
  );

  // Internal function to handle sending a message directly
  const handleSendMessage = useCallback(
    async (args: {
      message: Message;
      currentAssets: AssetFile[];
      /**
       * Prevents adding the message to the state.
       * This is useful when the message is already in message state and we're
       * resending it to the server.
       */
      skipAddingMessage?: boolean;
    }) => {
      // Add the message to the state
      if (!args.skipAddingMessage) {
        addMessage(args.message);
      }

      // Use streaming if available, otherwise fall back to non-streaming
      if (streamMessageToAI) {
        // Streaming mode
        const controller = new AbortController();
        setAbortController(controller);
        // Start with "loading" status - will switch to "streaming" when content arrives
        // Only show loading if NOT in human support mode
        if (!isHumanSupportMode) {
          setResponseStatus("loading");
        }

        // Create a placeholder message for streaming content
        const streamingMessageId = new ObjectId().toString();
        let streamingContent = "";
        let hasReceivedContent = false;
        const toolCalls: {
          toolCallId: string;
          toolName: string;
          input: unknown;
        }[] = [];

        try {
          const stream = streamMessageToAI({
            messages: [...messages, args.message],
            newMessages: [args.message],
            signal: controller.signal,
          });

          for await (const event of stream) {
            switch (event.type) {
              case "text-delta": {
                // Switch to streaming status when first content arrives
                if (!hasReceivedContent) {
                  hasReceivedContent = true;
                  setResponseStatus("streaming");
                }
                streamingContent += event.textDelta;
                // Update the streaming message in place
                setMessages((prev) => {
                  const existingIndex = prev.findIndex(
                    (m) => m.id === streamingMessageId,
                  );
                  const updatedMessage: Message = {
                    id: streamingMessageId,
                    role: "assistant",
                    content: streamingContent,
                  };
                  if (existingIndex >= 0) {
                    const updated = [...prev];
                    updated[existingIndex] = updatedMessage;
                    return updated;
                  } else {
                    return [...prev, updatedMessage];
                  }
                });
                break;
              }
              case "tool-call": {
                // Don't switch to streaming status for tool calls - keep showing loading
                // animation with status updates until actual text content arrives
                toolCalls.push(event.toolCall);
                // Update message with tool calls
                setMessages((prev) => {
                  const existingIndex = prev.findIndex(
                    (m) => m.id === streamingMessageId,
                  );
                  const content: AssistantMessageContent[] = [];
                  if (streamingContent) {
                    content.push({ type: "text", text: streamingContent });
                  }
                  for (const tc of toolCalls) {
                    content.push({
                      type: "tool-call",
                      toolCallId: tc.toolCallId,
                      toolName: tc.toolName,
                      input: tc.input as Json,
                    });
                  }
                  const updatedMessage: Message = {
                    id: streamingMessageId,
                    role: "assistant",
                    content: content.length === 1 ? streamingContent : content,
                  };
                  if (existingIndex >= 0) {
                    const updated = [...prev];
                    updated[existingIndex] = updatedMessage;
                    return updated;
                  } else {
                    return [...prev, updatedMessage];
                  }
                });
                break;
              }
              case "complete": {
                // Replace streaming message with final messages from server
                setMessages((prev) => {
                  // Remove the streaming placeholder
                  const withoutStreaming = prev.filter(
                    (m) => m.id !== streamingMessageId,
                  );
                  return [...withoutStreaming, ...event.messages];
                });
                // Process tool results in the complete response
                await handleToolResults(event.messages);
                break;
              }
              case "error": {
                console.error("Stream error:", event.error);
                addErrorMessage(event.error);
                break;
              }
              case "reasoning": {
                // Reasoning events can be logged or displayed in debug mode
                break;
              }
              case "status": {
                // Update the loading status message for dynamic status updates
                setLoadingStatusMessage(event.status);
                break;
              }
            }
          }

          // Clear the loading status message when streaming is complete
          setLoadingStatusMessage(undefined);
          setResponseStatus("idle");
          if (args.currentAssets.length > 0) {
            clearAssets();
          }
        } catch (error) {
          if (error instanceof Error && error.name === "AbortError") {
            // Stream was aborted by user, clean up streaming message
            setMessages((prev) =>
              prev.filter((m) => m.id !== streamingMessageId),
            );
            setLoadingStatusMessage(undefined);
            setResponseStatus("idle");
            return;
          }
          console.error("Error in streaming message:", error);
          setLoadingStatusMessage(undefined);
          setResponseStatus("error");
        } finally {
          setAbortController(null);
        }
      } else if (sendMessageToAI) {
        // Non-streaming mode (original implementation)
        setResponseStatus("loading");

        try {
          const response = await sendMessageToAI({
            messages: [...messages, args.message],
            newMessages: [args.message],
          });

          // Process tool results in the response
          await handleToolResults(response.messages);

          appendMessages(response.messages);
          setResponseStatus("idle");

          if (args.currentAssets.length > 0) {
            clearAssets();
          }
        } catch (error) {
          console.error("Error sending message:", error);

          setResponseStatus("error");
        }
      } else {
        console.error(
          "AiChatProvider: Either sendMessageToAI or streamMessageToAI must be provided",
        );
        setResponseStatus("error");
      }
    },
    [
      addMessage,
      addErrorMessage,
      appendMessages,
      sendMessageToAI,
      streamMessageToAI,
      clearAssets,
      handleToolResults,
      isHumanSupportMode,
      messages,
      setResponseStatus,
    ],
  );

  // Send message function from input field
  const sendMessage = useHandler(async (message?: string) => {
    if (inputValue.trim() !== "" || assets.length > 0 || message) {
      // Create a new message with text and asset images if available
      let userMessage: Message;

      if (assets.length > 0) {
        // Create message with content as an array of parts (text and images)
        const messageParts: UserMessageContent[] = [];

        // Add text part if there's input
        if (inputValue.trim() !== "") {
          messageParts.push({ type: "text", text: inputValue });
        }

        // Add file parts for each asset with a preview URL
        for (const asset of assets) {
          if (asset.previewUrl) {
            // Different handling based on file type
            if (!asset.fileType || asset.fileType === "image") {
              // Handle images like before
              messageParts.push({ type: "image", image: asset.previewUrl });

              /**
               * Need to add the image data to the message content as type text so that the llm
               * can reference the image's url and other metadata. Otherwise, the llm will not be able
               * to use the image information in the conversation.
               *
               * We hide this string in chat-message.tsx
               */
              messageParts.push({
                type: "text",
                text: `[[[[image-data:${JSON.stringify({
                  url: asset.previewUrl,
                  type: asset.file.type,
                  name: asset.file.name,
                  // analysis: asset.analysis?.text || "", //Not necessary since we are pushing an "image" message part above
                })}]]]]`,
              });
            } else if (asset.fileType === "video") {
              // Handle videos - store URL and metadata for rendering
              messageParts.push({
                type: "text",
                text: `[[[[video-data:${JSON.stringify({
                  url: asset.previewUrl,
                  type: asset.file.type,
                  name: asset.file.name,
                })}]]]]`,
              });
            } else {
              // Add the document data for LLM reference, also hidden in UI
              messageParts.push({
                type: "text",
                text: `[[[[document-data:${JSON.stringify({
                  url: asset.previewUrl,
                  type: asset.fileType,
                  name: asset.file.name,
                  analysis: asset.analysis?.text || "",
                  analyzedDocumentId: asset.analysis?._id,
                })}]]]]`,
              });
            }
          }
        }

        // 1. Create a tool-call message for "image upload"
        // 2. Create a tool-response message, with the urls of the images I uploaded.

        userMessage = {
          id: new ObjectId().toString(),
          role: "user",
          content: messageParts,
        };
      } else {
        // Simple text-only message
        userMessage = {
          id: new ObjectId().toString(),
          role: "user",
          content: message || inputValue,
        };
      }

      // Clear input and assets before sending to avoid issues with useEffect
      setInputValue("");

      // Store the assets for the message but clear them from the UI immediately
      const currentAssets = [...assets];
      clearAssets();

      // Send the message
      await handleSendMessage({ message: userMessage, currentAssets });

      // // After sending, focus the input again
      setTimeout(() => {
        const inputElement = document.querySelector(
          'input[name="chat-message-input"]',
        ) as HTMLInputElement;
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }
  });

  // Ref to track if auto-start has already run
  const hasAutoStarted = useRef(false);

  // Auto start conversation if there is an initial message, autoStart is true, and there is only 1 default message
  useEffect(() => {
    // Skip if we've already auto-started
    if (hasAutoStarted.current) {
      return;
    }

    // If we should auto-start and we have exactly one default message
    // and the messages array has the same length (means it's just the initial load)
    if (autoStart && defaultMessages.length === 1 && messages.length === 1) {
      // Mark that we've auto-started
      hasAutoStarted.current = true;

      const initialMessage = defaultMessages[0];

      // Create a copy of the message to avoid directly using the defaultMessage
      // which is already in messages state, which would create a duplicate
      const messageToSend: Message = {
        id: Date.now().toString(),
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        role: initialMessage.role as "user",
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        content: initialMessage.content as string,
      };

      // Now send the message
      handleSendMessage({
        message: messageToSend,
        currentAssets: assets,
        skipAddingMessage: true,
      }).catch((error) => {
        console.error("Error sending initial message:", error);
        setResponseStatus("error");
      });
    }
  }, [
    autoStart,
    defaultMessages,
    messages,
    assets,
    handleSendMessage,
    setResponseStatus,
  ]);

  // Send tool response function
  const sendToolResponse = useCallback(
    async (response: ToolResponseArgs, options?: ToolResponseOptions) => {
      try {
        setResponseStatus("loading");

        // Create a tool response message
        const toolResponseMessage: Message = {
          id: new ObjectId().toString(),
          role: "tool",
          content: [
            {
              type: "tool-result",
              toolName: response.toolName,
              toolCallId: response.toolCallId,
              output: createToolResultOutput(response.result),
            },
          ],
        };

        // Add the tool response to messages
        addMessage(toolResponseMessage);

        if (!options?.skipHandleToolResultsCallback) {
          // Process the single tool response message
          await handleToolResults([toolResponseMessage]);
        }

        if (!options?.skipLlmCall && sendMessageToAI) {
          // Determine what messages to send to the AI
          // If prependToolCall is provided, we need to include it explicitly
          const messagesToSend = options?.prependToolCall
            ? [...messages, options.prependToolCall, toolResponseMessage]
            : [...messages, toolResponseMessage];

          // Send the updated messages to the AI
          const aiResponse = await sendMessageToAI({
            messages: messagesToSend,
            newMessages: options?.prependToolCall
              ? [options.prependToolCall, toolResponseMessage]
              : [toolResponseMessage],
          });

          if (!options?.skipHandleToolResultsCallback) {
            // Process tool results in the AI response
            await handleToolResults(aiResponse.messages);
          }

          // Add the AI's response to the messages
          appendMessages(aiResponse.messages);
        }

        setResponseStatus("idle");
      } catch (error) {
        console.error("Error sending tool response:", error);
        setResponseStatus("error");
      }
    },
    [messages, addMessage, appendMessages, sendMessageToAI, handleToolResults],
  );

  return (
    <AiChatContext.Provider
      value={{
        // Human support mode
        isHumanSupportMode,

        // Assets
        assets,
        addAssets,
        removeAsset,
        clearAssets,
        uploadFiles,

        // File upload options
        fileUploadOptions,
        uploader,

        // Input
        inputValue,
        setInputValue,

        // Voice recording
        isRecording,
        startRecording,
        stopRecording,
        onSpeechToText,

        // Send message
        sendMessage,
        stopStream,
        sendToolResponse,

        // Messages
        messages,
        addMessage,
        updateMessage,
        clearMessages,

        // Response status
        responseStatus,
        loadingStatusMessage,

        previewStatus,
        setPreviewStatus,

        // Tool callbacks
        onToolResult: onToolResult || (async () => {}),

        // Additional tool components
        additionalToolComponents,
        markdownLinkComponent,
        markdownTextTransform,

        // Feedback
        onMessageFeedback,
        conversationId,
      }}
    >
      {children}
    </AiChatContext.Provider>
  );
};
